/**
 * RentEventHomePage duck — feeds the homepage "Featured kits" section
 * (2026-07-02, owner decision: it REPLACES the occasion tile grid —
 * the hero already offers occasion entry twice, and with a small
 * catalog the tiles mostly dead-ended).
 *
 * Newest published listings, queried with the same fields/variants as
 * the search page so the shared ListingCard renders identically.
 * Uses the global marketplaceData entity store; this slice keeps refs.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const FEATURED_COUNT = 4;

const entityRefs = entities =>
  entities.map(entity => ({ id: entity.id, type: entity.type }));

// ─── Thunks ───

export const queryFeaturedListings = createAsyncThunk(
  'RentEventHomePage/queryFeaturedListings',
  async (config, { dispatch, extra: sdk, rejectWithValue }) => {
    try {
      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config?.layout?.listingImage || {};
      const aspectRatio = aspectHeight / aspectWidth;

      const response = await sdk.listings.query({
        perPage: FEATURED_COUNT,
        include: ['author', 'images'],
        'fields.listing': [
          'title',
          'geolocation',
          'price',
          'deleted',
          'state',
          'publicData.listingType',
          'publicData.location',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          'publicData.cardStyle',
          'publicData.pickupEnabled',
          'publicData.shippingEnabled',
          'publicData.priceVariationsEnabled',
          'publicData.priceVariants',
        ],
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      });
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ─── Slice ───

const rentEventHomePageSlice = createSlice({
  name: 'RentEventHomePage',
  initialState: {
    featuredListingRefs: [],
    queryFeaturedInProgress: false,
    queryFeaturedError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryFeaturedListings.pending, state => {
        state.queryFeaturedInProgress = true;
        state.queryFeaturedError = null;
      })
      .addCase(queryFeaturedListings.fulfilled, (state, action) => {
        state.queryFeaturedInProgress = false;
        state.featuredListingRefs = entityRefs(action.payload.data.data);
      })
      .addCase(queryFeaturedListings.rejected, (state, action) => {
        state.queryFeaturedInProgress = false;
        state.queryFeaturedError = action.payload;
      });
  },
});

export default rentEventHomePageSlice.reducer;

// ─── loadData (SSR + client) ───

export const loadData = (params, search, config) => dispatch => {
  return dispatch(queryFeaturedListings(config));
};
